import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { getRegions } from "../lib/adventureApi";
import { clearToken, getUser } from "../lib/auth";
import { setAgency } from "../lib/agency";
import { ArrowUpRight, Compass, MapPin, Route, LogOut, Briefcase } from "lucide-react";

export default function Landing() {
  const [regions, setRegions] = useState([]);
  const navigate = useNavigate();
  const user = getUser();

  useEffect(() => {
    getRegions().then(setRegions).catch(() => {});
  }, []);

  const logout = () => { clearToken(); setAgency({}); window.location.reload(); };

  return (
    <div className="min-h-screen bg-base text-ink relative grain overflow-x-hidden" data-testid="landing-page">
      {/* Nav */}
      <header className="relative z-10 flex items-center justify-between px-6 sm:px-10 py-6 border-b border-sand-dark">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-terracotta rounded-sm flex items-center justify-center">
            <Compass className="w-5 h-5 text-white" strokeWidth={2.2} />
          </div>
          <span className="font-display font-extrabold text-lg tracking-tight">WAYFINDER</span>
          <span className="ml-1 font-mono text-[10px] tracking-[0.2em] uppercase bg-moss/10 border border-moss/30 text-moss rounded-full px-2 py-0.5">
            Builder
          </span>
        </div>
        <div className="flex items-center gap-4 sm:gap-5">
          <a href="/" data-testid="build-home-link" className="font-mono text-[11px] tracking-[0.2em] uppercase text-ink-muted hover:text-terracotta transition-colors">Home</a>
          {user ? (
            <>
              <span className="hidden md:flex items-center gap-1.5 font-mono text-[11px] tracking-[0.15em] uppercase text-ink-muted">
                <Briefcase className="w-3.5 h-3.5 text-moss" /> {user.company_name || user.name || "Trade partner"}
              </span>
              <a href="/admin" data-testid="owner-link" className="font-mono text-[11px] tracking-[0.2em] uppercase text-ink-muted hover:text-terracotta transition-colors">Owner</a>
              <button data-testid="logout-btn" onClick={logout} className="flex items-center gap-1.5 font-mono text-[11px] tracking-[0.2em] uppercase text-ink-muted hover:text-terracotta transition-colors">
                <LogOut className="w-3.5 h-3.5" /> Sign out
              </button>
            </>
          ) : (
            <a href="/trade" data-testid="build-tradelogin-link" className="flex items-center gap-1.5 font-mono text-[11px] tracking-[0.2em] uppercase text-moss hover:text-terracotta transition-colors">
              <Briefcase className="w-3.5 h-3.5" /> Trade login
            </a>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 px-6 sm:px-10 pt-16 sm:pt-24 pb-12 max-w-6xl">
        <motion.p
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="font-mono text-xs tracking-[0.25em] uppercase text-moss mb-5"
        >
          Partner Portal · Nepal · Central Asia · The Silk Road
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
          className="font-display font-extrabold text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-4xl"
        >
          Build your client's journey<br />on the map, not a form.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}
          className="mt-6 text-lg text-ink-secondary max-w-2xl leading-relaxed"
        >
          Drag ready-made treks, stays and experiences onto a living map. We route the transfers,
          pace the altitude and price it live — then hand you a branded, client-ready proposal in minutes.
        </motion.p>

        <div className="mt-8 flex flex-wrap gap-6 text-sm text-ink-secondary">
          <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-terracotta" /> Interactive map builder</span>
          <span className="flex items-center gap-2"><Route className="w-4 h-4 text-terracotta" /> Live trade pricing</span>
          <span className="flex items-center gap-2"><Briefcase className="w-4 h-4 text-terracotta" /> White-label proposals</span>
        </div>
      </section>

      {/* Region grid */}
      <section className="relative z-10 px-6 sm:px-10 pb-24 max-w-6xl">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display font-bold text-2xl tracking-tight">Choose a destination for your client</h2>
          <span className="font-mono text-xs text-ink-muted">{regions.length} regions</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {regions.map((r, i) => (
            <motion.button
              key={r.id}
              data-testid={`region-card-${r.id}`}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.08 * i }}
              onClick={() => navigate(`/build/${r.id}`)}
              className="group text-left relative overflow-hidden rounded-md border border-sand-dark bg-white hover:border-terracotta transition-colors"
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={r.image} alt={r.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
                <div className="absolute top-4 left-4 flex items-center justify-center w-9 h-9 bg-white/85 backdrop-blur-sm rounded-sm">
                  <ArrowUpRight className="w-5 h-5 text-ink group-hover:text-terracotta transition-colors" />
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-display font-bold text-xl tracking-tight">{r.name}</h3>
                <p className="mt-1.5 text-sm text-ink-secondary leading-relaxed">{r.tagline}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </section>
    </div>
  );
}
